import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface AylikVeri {
  ay: string;
  yil: number;
  ayNo: number;
  servisSayisi: number;
  toplamKazanc: number;
  ortalamaUcret: number;
}

export interface IslemDagilim {
  islem: string;
  sayi: number;
  yuzde: number;
  renk: string;
}

export interface OdemeOzet {
  tip: string;
  sayi: number;
  toplam: number;
}

export interface GenelOzet {
  toplamServis: number;
  buAyServis: number;
  buAyKazanc: number;
  gecenAyKazanc: number;
  toplamKazanc: number;
  ortalamaUcret: number;
  enCokIslem: string;
  enCokIslemSayi: number;
}

const ayAdlari = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

const islemRenkler: Record<string, string> = {
  'Filtre Bakımı – Dörtlü': '#0ea5e9',
  'Filtre Bakımı – Beşli': '#6366f1',
  'Genel Bakım': '#10b981',
  'Yeni Montaj': '#8b5cf6',
  'Bina Girişi': '#14b8a6',
  'Bina Girişi Bakım': '#06b6d4',
  'Taşıma': '#f59e0b',
  'Diğer': '#94a3b8',
};

export function useRaporlar() {
  const [servisler, setServisler] = useState<any[]>([]);
  const [musteriler, setMusteriler] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: sData, error: sErr } = await supabase
        .from('sadem_servis_kayitlari')
        .select('*')
        .eq('aktif', true)
        .order('servis_tarihi', { ascending: false });

      if (sErr) throw sErr;

      const { data: mData, error: mErr } = await supabase
        .from('sadem_musteriler')
        .select('id')
        .eq('aktif', true).eq('silindi', false);

      if (mErr) throw mErr;

      setServisler(sData || []);
      setMusteriler(mData || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getAylikVeriler = (sonKacAy = 12): AylikVeri[] => {
    const result: AylikVeri[] = [];
    const now = new Date();
    for (let i = sonKacAy - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const yil = d.getFullYear();
      const ayNo = d.getMonth();
      const ayServisleri = servisler.filter((s: any) => {
        if (!s.servis_tarihi) return false;
        const sd = new Date(s.servis_tarihi);
        return sd.getFullYear() === yil && sd.getMonth() === ayNo;
      });
      const toplamKazanc = ayServisleri.reduce((sum: number, s: any) => sum + (Number(s.alinan_ucret) || 0), 0);
      const servisSayisi = ayServisleri.length;
      result.push({
        ay: ayAdlari[ayNo], yil, ayNo, servisSayisi, toplamKazanc,
        ortalamaUcret: servisSayisi > 0 ? Math.round(toplamKazanc / servisSayisi) : 0,
      });
    }
    return result;
  };

  const getIslemDagilimi = (): IslemDagilim[] => {
    const sayac: Record<string, number> = {};
    servisler.forEach((s: any) => { sayac[s.islem_turu] = (sayac[s.islem_turu] || 0) + 1; });
    const toplam = servisler.length;
    return Object.entries(sayac)
      .sort((a, b) => b[1] - a[1])
      .map(([islem, sayi]) => ({ islem, sayi, yuzde: toplam > 0 ? Math.round((sayi / toplam) * 100) : 0, renk: islemRenkler[islem] || '#94a3b8' }));
  };

  const getOdemeOzeti = (): OdemeOzet[] => {
    const sayac: Record<string, { sayi: number; toplam: number }> = {};
    servisler.forEach((s: any) => {
      const tip = s.odeme_tipi || 'Belirtilmemiş';
      if (!sayac[tip]) sayac[tip] = { sayi: 0, toplam: 0 };
      sayac[tip].sayi++;
      sayac[tip].toplam += Number(s.alinan_ucret) || 0;
    });
    return Object.entries(sayac).map(([tip, v]) => ({ tip, sayi: v.sayi, toplam: v.toplam }));
  };

  const getGenelOzet = (): GenelOzet => {
    const now = new Date();
    const buAyNo = now.getMonth();
    const buAyYil = now.getFullYear();
    const gecenAy = new Date(buAyYil, buAyNo - 1, 1);

    const buAyServisler = servisler.filter((s: any) => {
      if (!s.servis_tarihi) return false;
      const d = new Date(s.servis_tarihi);
      return d.getFullYear() === buAyYil && d.getMonth() === buAyNo;
    });
    const gecenAyServisler = servisler.filter((s: any) => {
      if (!s.servis_tarihi) return false;
      const d = new Date(s.servis_tarihi);
      return d.getFullYear() === gecenAy.getFullYear() && d.getMonth() === gecenAy.getMonth();
    });

    const buAyKazanc = buAyServisler.reduce((sum: number, s: any) => sum + (Number(s.alinan_ucret) || 0), 0);
    const gecenAyKazanc = gecenAyServisler.reduce((sum: number, s: any) => sum + (Number(s.alinan_ucret) || 0), 0);
    const toplamKazanc = servisler.reduce((sum: number, s: any) => sum + (Number(s.alinan_ucret) || 0), 0);
    const ortalamaUcret = servisler.length > 0 ? Math.round(toplamKazanc / servisler.length) : 0;

    const islemSayac: Record<string, number> = {};
    servisler.forEach((s: any) => { islemSayac[s.islem_turu] = (islemSayac[s.islem_turu] || 0) + 1; });
    const enCokIslem = Object.entries(islemSayac).sort((a, b) => b[1] - a[1])[0];

    return {
      toplamServis: servisler.length,
      buAyServis: buAyServisler.length,
      buAyKazanc,
      gecenAyKazanc,
      toplamKazanc,
      ortalamaUcret,
      enCokIslem: enCokIslem ? enCokIslem[0] : '-',
      enCokIslemSayi: enCokIslem ? enCokIslem[1] : 0,
    };
  };

  return { servisler, musteriler, loading, error, refetch: fetchData, getAylikVeriler, getIslemDagilimi, getOdemeOzeti, getGenelOzet };
}